import { BellRing, CheckCircle2, Mail, MessageCircle, Plus, Scale, TriangleAlert } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api } from "../../lib/api";
import { fmt, when } from "../../lib/format";
import { keys, useAction, useMe, useSaldo } from "../../lib/queries";
import { Badge, LevelBadge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { Dialog } from "../../ui/Dialog";
import { EmptyState, ErrorState, PageSkeleton, useToast } from "../../ui/Feedback";
import { Field, FormGrid, Input, Select } from "../../ui/Form";
import { Meter } from "../../ui/Misc";
import table from "../../ui/Table.module.css";
import { useProjectContext } from "./ProjectLayout";
import styles from "./SaldoTab.module.css";

const KIND_LABEL = { topup: "Top-up", adjustment: "Koreksi", refund: "Pengembalian" } as const;
const LEVEL_TEXT: Record<string, string> = { low: "≤30%", critical: "≤10%", empty: "habis" };
const QUICK_AMOUNTS = [100_000, 200_000, 500_000, 1_000_000];

export function SaldoTab() {
  const { project } = useProjectContext();
  const saldo = useSaldo(project.id);
  const me = useMe().data!;
  const canMoveMoney = me.role === "admin" || me.role === "finance";
  const toast = useToast();
  const [topupOpen, setTopupOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const remind = useAction(() => api.post<{ sent: boolean; deliveries: { status: string; detail: string }[] }>(`/projects/${project.id}/saldo/remind`), [keys.saldo(project.id)]);

  if (saldo.isPending) return <PageSkeleton />;
  if (saldo.isError) return <ErrorState error={saldo.error} />;
  const s = saldo.data;
  const ratio = s.lastTopupMilli ? s.balanceMilli / s.lastTopupMilli : 0;
  // Top-ups carried a fee until catalog v4; the column only shows for ledgers that still have one.
  const legacyFees = s.ledger.some((row) => row.serviceFeeMilli > 0);
  const rec = s.reconciliation;

  return (
    <div className={styles.layout}>
      <section className={styles.hero}>
        <div className={styles.heroMain}>
          <span className={styles.heroLabel}>Saldo AI {project.name}</span>
          <span className={styles.heroValue}>{fmt.rupiah(s.balanceMilli)}</span>
          <div className={styles.heroMeter}>
            <Meter ratio={ratio} level={s.level} label="Sisa saldo dari top-up terakhir" />
          </div>
          <div className={styles.heroFacts}>
            <LevelBadge level={s.level} />
            <span>{fmt.percent(Math.min(1, ratio))} dari top-up terakhir {fmt.rupiah(s.lastTopupMilli)}</span>
            <span>{s.daysLeft === null ? "belum ada pemakaian 7 hari terakhir" : s.daysLeft === 0 ? "chatbot berhenti, tombol WhatsApp tampil" : `±${s.daysLeft} hari lagi`}</span>
            <span>
              cukup ±{fmt.int(s.conversationsCovered)} percakapan (perkiraan {fmt.rupiah(s.estimatedCostPerConversationMilli)} per percakapan{project.linkage.enabled ? ", termasuk Linkage" : ""})
            </span>
          </div>
        </div>
        {canMoveMoney && (
        <div className={styles.heroActions}>
          <Button variant="primary" icon={<Plus aria-hidden />} onClick={() => setTopupOpen(true)}>
            Catat top-up
          </Button>
          <Button
            icon={<BellRing aria-hidden />}
            loading={remind.isPending}
            disabled={s.level === "ok"}
            title={s.level === "ok" ? "Saldo masih aman" : undefined}
            onClick={async () => {
              const result = await remind.mutateAsync(undefined);
              toast(result.sent ? `Pengingat dikirim: ${result.deliveries.map((d) => d.status).join(", ")}` : "Tidak ada pengingat untuk dikirim.");
            }}
          >
            Kirim ulang pengingat
          </Button>
          <Button variant="ghost" icon={<Scale aria-hidden />} onClick={() => setAdjustOpen(true)}>
            Koreksi / pengembalian
          </Button>
        </div>
        )}
      </section>

      <div className={styles.grid}>
        <Card title="Buku saldo" description="Top-up, koreksi, dan pengembalian. Pemakaian per percakapan tercatat di Ringkasan dan Percakapan." flush>
          {s.ledger.length === 0 ? (
            <EmptyState title="Belum ada transaksi" />
          ) : (
            <div className={table.wrap}>
              <table className={table.table}>
                <thead>
                  <tr>
                    <th className={table.first}>Tanggal</th>
                    <th>Jenis</th>
                    <th>Referensi</th>
                    <th className={table.right}>Nominal saldo</th>
                    {legacyFees && <th className={table.right}>Biaya layanan (v3)</th>}
                    <th className={`${table.right} ${table.last}`}>Saldo setelah</th>
                  </tr>
                </thead>
                <tbody>
                  {s.ledger.map((row) => (
                    <tr key={row.id}>
                      <td className={table.first} style={{ whiteSpace: "nowrap" }}>
                        {when.dateTime(row.createdAt)}
                        {row.createdBy && <div className={table.muted}>{row.createdBy}</div>}
                      </td>
                      <td>
                        <Badge tone={row.kind === "topup" ? "good" : "neutral"}>{KIND_LABEL[row.kind]}</Badge>
                        {row.note && <div className={table.muted}>{row.note}</div>}
                      </td>
                      <td className={table.muted}>{row.reference ?? "—"}</td>
                      <td className={table.right}>{fmt.rupiah(row.amountMilli)}</td>
                      {legacyFees && <td className={table.right}>{row.serviceFeeMilli ? fmt.rupiah(row.serviceFeeMilli) : "—"}</td>}
                      <td className={`${table.right} ${table.last}`}>{fmt.rupiah(row.balanceAfterMilli)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className={styles.side}>
          <Card title="Rekonsiliasi" description="Saldo adalah dana titipan: harus selalu bisa dijelaskan.">
            <dl className={styles.recon}>
              <dt>Total top-up &amp; koreksi</dt>
              <dd>{fmt.rupiah(rec.ledgerTotalMilli)}</dd>
              <dt>Total dipotong pemakaian</dt>
              <dd>− {fmt.rupiah(rec.usageTotalMilli)}</dd>
              <dt className={styles.reconTotal}>Saldo seharusnya</dt>
              <dd className={styles.reconTotal}>{fmt.rupiah(rec.expectedBalanceMilli)}</dd>
            </dl>
            {rec.driftMilli === 0 ? (
              <p className={styles.reconOk}>
                <CheckCircle2 aria-hidden /> Cocok dengan saldo tercatat.
              </p>
            ) : (
              <p className={styles.reconBad}>
                <TriangleAlert aria-hidden /> Selisih {fmt.rupiah(rec.driftMilli)} — periksa audit log sebelum tutup buku.
              </p>
            )}
            {canMoveMoney && (
              <dl className={`${styles.recon} ${styles.reconSecondary}`}>
                <dt>Biaya penyedia untuk pengunjung</dt>
                <dd>{fmt.rupiah(rec.visitorProviderCostMilli)}</dd>
                <dt>Pendapatan pemakaian (faktor {String(s.saldoFactor).replace(".", ",")})</dt>
                <dd>{fmt.rupiah(rec.usageMarginMilli)}</dd>
                <dt>Biaya penyedia termasuk uji</dt>
                <dd>{fmt.rupiah(rec.providerCostMilli)}</dd>
                {rec.serviceFeesMilli > 0 && (
                  <>
                    <dt>Biaya layanan top-up (v3)</dt>
                    <dd>{fmt.rupiah(rec.serviceFeesMilli)}</dd>
                  </>
                )}
              </dl>
            )}
          </Card>

          <Card title="Pengingat terkirim" description="Otomatis saat saldo ≤30%, ≤10%, dan habis — lewat WhatsApp dan email PIC.">
            {s.reminders.length === 0 ? (
              <EmptyState title="Belum ada pengingat" />
            ) : (
              <ul className={styles.reminders}>
                {s.reminders.map((reminder, i) => (
                  <li key={i}>
                    {reminder.channel === "whatsapp" ? <MessageCircle aria-hidden /> : <Mail aria-hidden />}
                    <div>
                      <div>
                        Saldo {LEVEL_TEXT[reminder.level] ?? reminder.level} · {fmt.rupiah(reminder.balanceMilli)}{" "}
                        <Badge tone={reminder.status === "sent" ? "good" : reminder.status === "failed" ? "critical" : "neutral"}>{reminder.status === "sent" ? "terkirim" : reminder.status === "failed" ? "gagal" : "dilewati"}</Badge>
                      </div>
                      <div className={table.muted}>
                        {when.dateTime(reminder.createdAt)} · {reminder.detail}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <TopupDialog open={topupOpen} onClose={() => setTopupOpen(false)} projectId={project.id} />
      <AdjustDialog open={adjustOpen} onClose={() => setAdjustOpen(false)} projectId={project.id} />
    </div>
  );
}

/** Catalog v4: top up any time, any amount, with no service fee. What the client transfers is what lands in the saldo. */
function TopupDialog({ open, onClose, projectId }: { open: boolean; onClose: () => void; projectId: string }) {
  const toast = useToast();
  const [amount, setAmount] = useState(200_000);
  const save = useAction((body: Record<string, unknown>) => api.post(`/projects/${projectId}/saldo/topups`, body), [keys.saldo(projectId), keys.project(projectId), ["overview"]]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await save.mutateAsync({ amountRupiah: amount, reference: form.get("reference") || undefined, note: form.get("note") || undefined });
    toast(`Top-up ${fmt.rupiah(amount * 1000)} dicatat. Chatbot aktif kembali dan siklus pengingat dimulai ulang.`);
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Catat top-up saldo"
      description="Catat setelah transfer terverifikasi di rekening. Saldo bertambah seketika."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button variant="primary" type="submit" form="topup" loading={save.isPending}>
            Catat top-up
          </Button>
        </>
      }
    >
      <form id="topup" onSubmit={submit} style={{ display: "grid", gap: 16 }}>
        <div className={styles.quick}>
          {QUICK_AMOUNTS.map((value) => (
            <button key={value} type="button" aria-pressed={amount === value} onClick={() => setAmount(value)}>
              {fmt.rupiah(value * 1000)}
            </button>
          ))}
        </div>
        <FormGrid>
          <Field label="Nominal saldo (Rp)" hint="Disarankan minimal Rp 100.000.">
            {(id, describedBy) => <Input id={id} type="number" min={1000} step={1000} value={amount} onChange={(event) => setAmount(Number(event.target.value))} aria-describedby={describedBy} required />}
          </Field>
          <Field label="Referensi transfer">{(id) => <Input id={id} name="reference" placeholder="INV/… atau no. mutasi" />}</Field>
        </FormGrid>
        <Field label="Catatan">{(id) => <Input id={id} name="note" placeholder="Opsional" />}</Field>
        <div className={styles.summary}>
          <div>
            <span>Masuk ke saldo klien</span>
            <strong>{fmt.rupiah(amount * 1000)}</strong>
          </div>
          <div>
            <span>Biaya layanan top-up</span>
            <strong>Rp 0</strong>
          </div>
          <div className={styles.summaryTotal}>
            <span>Total ditagih ke klien</span>
            <strong>{fmt.rupiah(amount * 1000)}</strong>
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function AdjustDialog({ open, onClose, projectId }: { open: boolean; onClose: () => void; projectId: string }) {
  const toast = useToast();
  const [kind, setKind] = useState<"adjustment" | "refund">("adjustment");
  const save = useAction((body: Record<string, unknown>) => api.post(`/projects/${projectId}/saldo/adjustments`, body), [keys.saldo(projectId), keys.project(projectId), ["overview"]]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const amount = Number(form.get("amount"));
    await save.mutateAsync({ kind, amountRupiah: kind === "refund" ? -Math.abs(amount) : amount, note: form.get("note"), reference: form.get("reference") || undefined });
    toast(kind === "refund" ? "Pengembalian saldo dicatat." : "Koreksi saldo dicatat.");
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Koreksi atau pengembalian"
      description="Tercatat di audit log dengan nama Anda. Wajib menulis alasan."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button variant="primary" type="submit" form="adjust" loading={save.isPending}>
            Simpan
          </Button>
        </>
      }
    >
      <form id="adjust" onSubmit={submit} style={{ display: "grid", gap: 16 }}>
        <FormGrid>
          <Field label="Jenis">
            {(id) => (
              <Select id={id} value={kind} onChange={(event) => setKind(event.target.value as typeof kind)}>
                <option value="adjustment">Koreksi (+/−)</option>
                <option value="refund">Pengembalian ke klien</option>
              </Select>
            )}
          </Field>
          <Field label="Nominal (Rp)" hint={kind === "adjustment" ? "Negatif untuk mengurangi." : "Dikurangkan dari saldo."}>
            {(id, describedBy) => <Input id={id} name="amount" type="number" required aria-describedby={describedBy} />}
          </Field>
        </FormGrid>
        <Field label="Alasan" hint="Mis. saldo terpotong serangan bot yang seharusnya tertahan (Lampiran III Pasal 7 ayat 2).">
          {(id, describedBy) => <Input id={id} name="note" required minLength={5} aria-describedby={describedBy} />}
        </Field>
        <Field label="Referensi">{(id) => <Input id={id} name="reference" />}</Field>
        {save.isError && <p style={{ color: "var(--critical-ink)" }}>{save.error.message}</p>}
      </form>
    </Dialog>
  );
}
